import { useState, useRef, useEffect, useCallback } from 'react';
import { Pencil, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PxMark } from '@/components/PxMark';

interface EditableProjectNameProps {
    name: string;
    onRename: (newName: string) => Promise<{ success: boolean; error?: string }>;
    className?: string;
}

export function EditableProjectName({ name, onRename, className }: EditableProjectNameProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(name);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Update editValue when name prop changes
    useEffect(() => {
        if (!isEditing) {
            setEditValue(name);
        }
    }, [name, isEditing]);

    // Focus input when entering edit mode
    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    const startEditing = useCallback(() => {
        setIsEditing(true);
        setEditValue(name);
        setError(null);
    }, [name]);

    const cancelEditing = useCallback(() => {
        setIsEditing(false);
        setEditValue(name);
        setError(null);
    }, [name]);

    const saveChanges = useCallback(async () => {
        const trimmedValue = editValue.trim();

        // No change
        if (trimmedValue === name) {
            setIsEditing(false);
            return;
        }

        // Empty name
        if (!trimmedValue) {
            setError('Le nom ne peut pas être vide');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const result = await onRename(trimmedValue);

            if (result.success) {
                setIsEditing(false);
            } else {
                setError(result.error || 'Erreur lors du renommage');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erreur');
        } finally {
            setIsLoading(false);
        }
    }, [editValue, name, onRename]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            saveChanges();
        } else if (e.key === 'Escape') {
            cancelEditing();
        }
    }, [saveChanges, cancelEditing]);

    if (isEditing) {
        return (
            <div className="flex flex-col gap-1">
                <div className="flex items-center gap-1">
                    <input
                        ref={inputRef}
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onBlur={() => {
                            // Delay to allow button clicks to register
                            setTimeout(() => {
                                if (!isLoading) cancelEditing();
                            }, 150);
                        }}
                        className={cn(
                            "px-2 py-0.5 text-sm font-medium text-foreground bg-secondary border outline-none transition-colors",
                            "focus:border-foreground focus:ring-2 focus:ring-ring",
                            error ? "border-err" : "border-border",
                            className
                        )}
                        maxLength={50}
                        disabled={isLoading}
                    />

                    {isLoading ? (
                        <PxMark state="thinking" size={12} label="Renommage en cours" />
                    ) : (
                        <>
                            <button
                                onClick={saveChanges}
                                className="p-1 hover:bg-secondary transition-colors text-ok"
                                title="Sauvegarder"
                            >
                                <Check className="h-3.5 w-3.5" />
                            </button>
                            <button
                                onClick={cancelEditing}
                                className="p-1 hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
                                title="Annuler"
                            >
                                <X className="h-3.5 w-3.5" />
                            </button>
                        </>
                    )}
                </div>

                {error && (
                    <span className="font-mono text-[10px] text-err pl-1">{error}</span>
                )}
            </div>
        );
    }

    return (
        <div className="flex items-center gap-1.5 group">
            <span
                className={cn("text-sm font-medium text-foreground cursor-pointer", className)}
                onDoubleClick={startEditing}
            >
                {name}
            </span>
            <button
                onClick={startEditing}
                className="p-0.5 opacity-0 group-hover:opacity-100 hover:bg-secondary transition-opacity"
                title="Renommer"
            >
                <Pencil className="h-3 w-3 text-faint" />
            </button>
        </div>
    );
}
